import { motion } from "framer-motion";
import { ArrowRight, TrendingUp } from "lucide-react";
import { HERO_BG, heroChips } from "@/content";
import { Overline } from "@/components/site/Reveal";

const scrollTo = (id) => {
  const el = document.getElementById(id);
  if (window.__lenis && el) window.__lenis.scrollTo(el, { offset: -90 });
  else el?.scrollIntoView({ behavior: "smooth" });
};

export const Hero = () => {
  return (
    <section className="relative min-h-screen flex items-center overflow-hidden grain" data-testid="hero-section">
      <div className="absolute inset-0 overflow-hidden">
        <motion.img
          src={HERO_BG}
          alt=""
          className="w-full h-full object-cover"
          initial={{ scale: 1.05 }}
          animate={{ scale: 1.2 }}
          transition={{ duration: 16, ease: "easeInOut", repeat: Infinity, repeatType: "reverse" }}
        />
        <div className="absolute inset-0 bg-gradient-to-r from-ink/90 via-ink/50 to-ink/30" />
        <div className="absolute inset-0 bg-gradient-to-t from-ink/70 via-transparent to-transparent" />
        <div className="absolute inset-0" style={{ background: "radial-gradient(circle at 20% 30%, rgba(141,198,63,0.18) 0%, rgba(5,10,16,0) 55%)" }} />
      </div>

      <div className="relative z-10 mx-auto max-w-7xl px-6 pt-28 pb-16 w-full">
        <div className="max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }}>
            <Overline>The Intelligence Layer for Parking</Overline>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="mt-6 font-heading font-extrabold tracking-tighter text-4xl sm:text-5xl lg:text-7xl leading-[1.02]"
          >
            Turn Parking Into a<br />
            <span className="text-gradient">Real-Time Revenue Engine.</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
            className="mt-6 text-lg text-white/70 max-w-xl"
          >
            Intelligent pricing. Better utilization. Happier cities. Higher revenue. GeoDyn adds a
            geo-dynamic intelligence layer on top of the parking systems you already run, or can run on its own.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.3 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <button
              data-testid="hero-demo-cta"
              onClick={() => scrollTo("demo")}
              className="group inline-flex items-center gap-2 rounded-full bg-brand-green px-7 py-3.5 font-semibold text-black hover:bg-[#9fd94f] hover:shadow-[0_0_28px_rgba(141,198,63,0.5)] hover:-translate-y-0.5 transition-transform"
            >
              Request a Demo
              <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" />
            </button>
            <button
              data-testid="hero-how-cta"
              onClick={() => scrollTo("solution")}
              className="inline-flex items-center gap-2 rounded-full border border-white/20 px-7 py-3.5 font-semibold text-white hover:bg-white/5 hover:border-white/40 transition-colors"
            >
              <TrendingUp className="h-4 w-4 text-brand-cyan" /> See GeoDyn Vision
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-12 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-2xl"
          >
            {heroChips.map((c) => (
              <div key={c} className="rounded-lg border border-white/10 bg-white/[0.03] px-4 py-3 backdrop-blur-sm">
                <span className="font-mono text-[11px] tracking-wider uppercase text-white/70">{c}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>
    </section>
  );
};
